package com.fantasytenis.web.hooks

import androidx.compose.runtime.*
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.events.Event
import org.w3c.dom.events.KeyboardEvent
import org.w3c.dom.events.MouseEvent
import org.w3c.dom.events.WheelEvent
import kotlin.math.sqrt

/**
 * Configuration for canvas gesture handling.
 */
data class CanvasGestureConfig(
    val minZoom: Double = 0.15,
    val maxZoom: Double = 2.0,
    val zoomSensitivity: Double = 0.001,
    val panStep: Double = 50.0,
    val zoomStep: Double = 1.2
)

/**
 * State for canvas viewport.
 */
data class CanvasViewport(
    val panX: Double = 0.0,
    val panY: Double = 0.0,
    val zoom: Double = 1.0,
    val worldWidth: Double = 0.0,
    val worldHeight: Double = 0.0
) {
    /**
     * Calculate viewport to fit content in screen with padding.
     */
    fun fitToScreen(viewportWidth: Double, viewportHeight: Double, padding: Double = 40.0): CanvasViewport {
        if (worldWidth <= 0 || worldHeight <= 0 || viewportWidth <= 0 || viewportHeight <= 0) {
            return this
        }

        val availableWidth = viewportWidth - padding * 2
        val availableHeight = viewportHeight - padding * 2

        val scaleX = availableWidth / worldWidth
        val scaleY = availableHeight / worldHeight
        val newZoom = minOf(scaleX, scaleY).coerceIn(0.15, 2.0)

        val newPanX = (viewportWidth - worldWidth * newZoom) / 2
        val newPanY = (viewportHeight - worldHeight * newZoom) / 2

        return copy(panX = newPanX, panY = newPanY, zoom = newZoom)
    }

    /**
     * Zoom toward a specific point (cursor position).
     */
    fun zoomTowardPoint(
        pointX: Double,
        pointY: Double,
        zoomDelta: Double,
        config: CanvasGestureConfig
    ): CanvasViewport {
        val newZoom = (zoom * zoomDelta).coerceIn(config.minZoom, config.maxZoom)
        if (newZoom == zoom) return this

        val scaleDiff = newZoom / zoom
        val newPanX = pointX - (pointX - panX) * scaleDiff
        val newPanY = pointY - (pointY - panY) * scaleDiff

        return copy(panX = newPanX, panY = newPanY, zoom = newZoom)
    }
}

/**
 * Internal state for tracking gesture progress.
 */
private data class GestureState(
    val isPanning: Boolean = false,
    val lastX: Double = 0.0,
    val lastY: Double = 0.0,
    val initialPinchDistance: Double = 0.0,
    val initialPinchZoom: Double = 1.0
)

/**
 * Result from the canvas gestures hook.
 */
data class CanvasGestureHandlers(
    val onWheel: (Event) -> Unit,
    val onMouseDown: (Event) -> Unit,
    val onMouseMove: (Event) -> Unit,
    val onMouseUp: (Event) -> Unit,
    val onMouseLeave: (Event) -> Unit,
    val onTouchStart: (Event) -> Unit,
    val onTouchMove: (Event) -> Unit,
    val onTouchEnd: (Event) -> Unit,
    val isPanning: Boolean,
    val zoomIn: () -> Unit,
    val zoomOut: () -> Unit,
    val fitToScreen: () -> Unit
)

/**
 * Hook for handling pan/zoom gestures on a canvas.
 *
 * Supports:
 * - Mouse wheel zoom (zooms toward cursor)
 * - Click + drag pan
 * - Touch pinch-to-zoom
 * - Touch drag pan
 * - Keyboard navigation (arrows for pan, +/- for zoom, 0 for fit)
 */
@Composable
fun useCanvasGestures(
    viewport: CanvasViewport,
    onViewportChange: (CanvasViewport) -> Unit,
    viewportWidth: Double,
    viewportHeight: Double,
    config: CanvasGestureConfig = CanvasGestureConfig()
): CanvasGestureHandlers {
    var gestureState by remember { mutableStateOf(GestureState()) }

    // Keyboard event handler
    DisposableEffect(viewport, viewportWidth, viewportHeight) {
        val handleKeyDown: (Event) -> Unit = { event ->
            val keyEvent = event.unsafeCast<KeyboardEvent>()

            // Only handle if no input is focused
            val activeElement = document.activeElement?.tagName?.lowercase()
            val isInputFocused = activeElement == "input" || activeElement == "textarea"

            if (!isInputFocused) {
                when (keyEvent.key) {
                    "ArrowLeft" -> {
                        keyEvent.preventDefault()
                        onViewportChange(viewport.copy(panX = viewport.panX + config.panStep))
                    }
                    "ArrowRight" -> {
                        keyEvent.preventDefault()
                        onViewportChange(viewport.copy(panX = viewport.panX - config.panStep))
                    }
                    "ArrowUp" -> {
                        keyEvent.preventDefault()
                        onViewportChange(viewport.copy(panY = viewport.panY + config.panStep))
                    }
                    "ArrowDown" -> {
                        keyEvent.preventDefault()
                        onViewportChange(viewport.copy(panY = viewport.panY - config.panStep))
                    }
                    "+", "=" -> {
                        keyEvent.preventDefault()
                        val centerX = viewportWidth / 2
                        val centerY = viewportHeight / 2
                        onViewportChange(viewport.zoomTowardPoint(centerX, centerY, config.zoomStep, config))
                    }
                    "-", "_" -> {
                        keyEvent.preventDefault()
                        val centerX = viewportWidth / 2
                        val centerY = viewportHeight / 2
                        onViewportChange(viewport.zoomTowardPoint(centerX, centerY, 1.0 / config.zoomStep, config))
                    }
                    "0" -> {
                        keyEvent.preventDefault()
                        onViewportChange(viewport.fitToScreen(viewportWidth, viewportHeight))
                    }
                }
            }
        }

        window.addEventListener("keydown", handleKeyDown)

        onDispose {
            window.removeEventListener("keydown", handleKeyDown)
        }
    }

    // Mouse wheel handler - supports both trackpad gestures and mouse wheel
    val handleWheel: (Event) -> Unit = { event ->
        val wheelEvent = event.unsafeCast<WheelEvent>()
        wheelEvent.preventDefault()

        // Get mouse position relative to the canvas
        val target = wheelEvent.currentTarget.asDynamic()
        val rect = target.getBoundingClientRect()
        val mouseX = wheelEvent.clientX - (rect.left as Double)
        val mouseY = wheelEvent.clientY - (rect.top as Double)

        // Distinguish between trackpad pinch (zoom) and two-finger scroll (pan)
        // Browsers encode trackpad pinch gestures as wheel events with ctrlKey: true
        // Regular two-finger scroll has ctrlKey: false
        if (wheelEvent.ctrlKey || wheelEvent.metaKey) {
            // PINCH-TO-ZOOM: ctrlKey is set by browser for trackpad pinch
            // Also handles Ctrl+scroll for mouse users
            // Use smaller factor for smoother zoom with trackpad
            val zoomFactor = 1 - wheelEvent.deltaY * 0.01
            onViewportChange(viewport.zoomTowardPoint(mouseX, mouseY, zoomFactor, config))
        } else {
            // TWO-FINGER SCROLL on trackpad: pan the canvas
            // deltaX = horizontal scroll, deltaY = vertical scroll
            // For mouse wheel without Ctrl, this also pans (scroll up/down = pan up/down)
            onViewportChange(viewport.copy(
                panX = viewport.panX - wheelEvent.deltaX,
                panY = viewport.panY - wheelEvent.deltaY
            ))
        }
    }

    // Mouse down handler (start pan)
    val handleMouseDown: (Event) -> Unit = { event ->
        val mouseEvent = event.unsafeCast<MouseEvent>()

        // Only pan on left click (button 0)
        if (mouseEvent.button.toInt() == 0) {
            gestureState = gestureState.copy(
                isPanning = true,
                lastX = mouseEvent.clientX.toDouble(),
                lastY = mouseEvent.clientY.toDouble()
            )
        }
    }

    // Mouse move handler (pan)
    val handleMouseMove: (Event) -> Unit = { event ->
        if (gestureState.isPanning) {
            val mouseEvent = event.unsafeCast<MouseEvent>()

            val deltaX = mouseEvent.clientX.toDouble() - gestureState.lastX
            val deltaY = mouseEvent.clientY.toDouble() - gestureState.lastY

            onViewportChange(viewport.copy(
                panX = viewport.panX + deltaX,
                panY = viewport.panY + deltaY
            ))

            gestureState = gestureState.copy(
                lastX = mouseEvent.clientX.toDouble(),
                lastY = mouseEvent.clientY.toDouble()
            )
        }
    }

    // Mouse up handler (end pan)
    val handleMouseUp: (Event) -> Unit = {
        gestureState = gestureState.copy(isPanning = false)
    }

    // Mouse leave handler (end pan if mouse leaves canvas)
    val handleMouseLeave: (Event) -> Unit = {
        gestureState = gestureState.copy(isPanning = false)
    }

    // Touch start handler
    val handleTouchStart: (Event) -> Unit = { event ->
        val touchEvent = event.asDynamic()
        val touches = touchEvent.touches

        if (touches.length == 1) {
            // Single touch - start pan
            val touch = touches[0]
            gestureState = gestureState.copy(
                isPanning = true,
                lastX = touch.clientX as Double,
                lastY = touch.clientY as Double
            )
        } else if (touches.length == 2) {
            // Two touches - start pinch
            val touch1 = touches[0]
            val touch2 = touches[1]
            val distance = calculateDistance(
                touch1.clientX as Double, touch1.clientY as Double,
                touch2.clientX as Double, touch2.clientY as Double
            )
            gestureState = gestureState.copy(
                isPanning = false,
                initialPinchDistance = distance,
                initialPinchZoom = viewport.zoom
            )
        }
    }

    // Touch move handler
    val handleTouchMove: (Event) -> Unit = { event ->
        val touchEvent = event.asDynamic()
        val touches = touchEvent.touches

        if (touches.length == 1 && gestureState.isPanning) {
            // Single touch pan
            event.preventDefault()
            val touch = touches[0]
            val currentX = touch.clientX as Double
            val currentY = touch.clientY as Double

            val deltaX = currentX - gestureState.lastX
            val deltaY = currentY - gestureState.lastY

            onViewportChange(viewport.copy(
                panX = viewport.panX + deltaX,
                panY = viewport.panY + deltaY
            ))

            gestureState = gestureState.copy(
                lastX = currentX,
                lastY = currentY
            )
        } else if (touches.length == 2 && gestureState.initialPinchDistance > 0) {
            // Pinch zoom
            event.preventDefault()
            val touch1 = touches[0]
            val touch2 = touches[1]

            val currentDistance = calculateDistance(
                touch1.clientX as Double, touch1.clientY as Double,
                touch2.clientX as Double, touch2.clientY as Double
            )

            val scale = currentDistance / gestureState.initialPinchDistance
            val newZoom = (gestureState.initialPinchZoom * scale).coerceIn(config.minZoom, config.maxZoom)

            // Calculate pinch center
            val centerX = ((touch1.clientX as Double) + (touch2.clientX as Double)) / 2
            val centerY = ((touch1.clientY as Double) + (touch2.clientY as Double)) / 2

            // Get center relative to canvas
            val target = touchEvent.currentTarget
            val rect = target.getBoundingClientRect()
            val localCenterX = centerX - (rect.left as Double)
            val localCenterY = centerY - (rect.top as Double)

            // Calculate new pan to keep pinch center stable
            val scaleDiff = newZoom / viewport.zoom
            val newPanX = localCenterX - (localCenterX - viewport.panX) * scaleDiff
            val newPanY = localCenterY - (localCenterY - viewport.panY) * scaleDiff

            onViewportChange(viewport.copy(
                panX = newPanX,
                panY = newPanY,
                zoom = newZoom
            ))
        }
    }

    // Touch end handler
    val handleTouchEnd: (Event) -> Unit = { event ->
        val touchEvent = event.asDynamic()
        val touches = touchEvent.touches

        if (touches.length == 0) {
            gestureState = gestureState.copy(
                isPanning = false,
                initialPinchDistance = 0.0
            )
        } else if (touches.length == 1) {
            // Went from 2 to 1 touch - switch to pan mode
            val touch = touches[0]
            gestureState = gestureState.copy(
                isPanning = true,
                lastX = touch.clientX as Double,
                lastY = touch.clientY as Double,
                initialPinchDistance = 0.0
            )
        }
    }

    // Helper functions for UI controls
    val zoomIn: () -> Unit = {
        val centerX = viewportWidth / 2
        val centerY = viewportHeight / 2
        onViewportChange(viewport.zoomTowardPoint(centerX, centerY, config.zoomStep, config))
    }

    val zoomOut: () -> Unit = {
        val centerX = viewportWidth / 2
        val centerY = viewportHeight / 2
        onViewportChange(viewport.zoomTowardPoint(centerX, centerY, 1.0 / config.zoomStep, config))
    }

    val fitToScreen: () -> Unit = {
        onViewportChange(viewport.fitToScreen(viewportWidth, viewportHeight))
    }

    return CanvasGestureHandlers(
        onWheel = handleWheel,
        onMouseDown = handleMouseDown,
        onMouseMove = handleMouseMove,
        onMouseUp = handleMouseUp,
        onMouseLeave = handleMouseLeave,
        onTouchStart = handleTouchStart,
        onTouchMove = handleTouchMove,
        onTouchEnd = handleTouchEnd,
        isPanning = gestureState.isPanning,
        zoomIn = zoomIn,
        zoomOut = zoomOut,
        fitToScreen = fitToScreen
    )
}

/**
 * Calculate distance between two points.
 */
private fun calculateDistance(x1: Double, y1: Double, x2: Double, y2: Double): Double {
    val dx = x2 - x1
    val dy = y2 - y1
    return sqrt(dx * dx + dy * dy)
}
